import React, { useState, useEffect } from "react";
import { Settings, Lock, Bell, UserPlus } from "lucide-react";

export default function SettingsAdmin() {
  // ==========================
  // 🔥 All States
  // ==========================
  const [orderLock, setOrderLock] = useState(false);
  const [signupLock, setSignupLock] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [bkashActive, setBkashActive] = useState(true);
const [nagadActive, setNagadActive] = useState(true);
const [rocketActive, setRocketActive] = useState(true);
  const [savedMessage, setSavedMessage] = useState("");
  const [loading, setLoading] = useState(true);

  // 💳 Payment Numbers
  const [bkashNum, setBkashNum] = useState("");
  const [nagadNum, setNagadNum] = useState("");
  const [rocketNum, setRocketNum] = useState("");
  const [bkashInstruction, setBkashInstruction] = useState("");
  const [nagadInstruction, setNagadInstruction] = useState("");
  const [rocketInstruction, setRocketInstruction] = useState("");
  // ✏️ Edit Mode Controls
const [bkashEdit, setBkashEdit] = useState(false);
const [nagadEdit, setNagadEdit] = useState(false);
const [rocketEdit, setRocketEdit] = useState(false);
 // 🔥 Load All Settings
const loadSettings = async () => {
  try {
    const res = await fetch("https://api.speedfollowinfo.com/api/settings");
    const data = await res.json();

    if (data) {
      setOrderLock(Boolean(data.orderLock));
      setSignupLock(Boolean(data.signupLock));
      setAnnouncement(data.announcement || "");

      // 💳 Payment Numbers
      setBkashNum(data.bkash || "");
      setNagadNum(data.nagad || "");
      setRocketNum(data.rocket || "");

      // 🧾 Instructions
      setBkashInstruction(data.bkashInstruction || "");
      setNagadInstruction(data.nagadInstruction || "");
      setRocketInstruction(data.rocketInstruction || "");

      // 🔘 Active / On-Off
      setBkashActive(data.bkashActive ?? true);
      setNagadActive(data.nagadActive ?? true);
      setRocketActive(data.rocketActive ?? true);

      // 🔄 Reset Edit Buttons
      setBkashEdit(false);
      setNagadEdit(false);
      setRocketEdit(false);
    }
  } catch (err) {
    console.error("❌ Load Failed:", err);
    setSavedMessage("❌ Failed to load settings!");
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  loadSettings();
}, []);
  // ==========================
  // 🔥 SAVE SETTINGS
  // ==========================
  const handleSave = async () => {
  const settings = {
    orderLock,
    signupLock,
    announcement: announcement.trim(),

    // Payment Numbers
    bkash: bkashNum,
    nagad: nagadNum,
    rocket: rocketNum,

    // Payment Instructions
    bkashInstruction: bkashInstruction,
    nagadInstruction: nagadInstruction,
    rocketInstruction: rocketInstruction,
    // Payment Method Active Status
    bkashActive,
    nagadActive,
    rocketActive,
  };

  try {
    const res = await fetch("https://api.speedfollowinfo.com/api/settings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings),
    });

    const data = await res.json();

    if (data.success) {
      setSavedMessage("✅ Settings Updated Successfully!");
    } else {
      setSavedMessage("❌ Failed to update!");
    }
  } catch (err) {
    console.error(err);
    setSavedMessage("❌ Server Not Responding!");
  }

  setTimeout(() => setSavedMessage(""), 2500);
};

  // ==========================
  // 🔄 Loader
  // ==========================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0b0f1a] text-gray-300">
        <p className="animate-pulse text-lg">Loading Settings...</p>
      </div>
    );
  }

  // ==========================
  // UI STARTS
  // ==========================
  return (
    <div className="min-h-screen bg-[#0b0f1a] text-white p-6 md:p-10">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <Settings size={24} /> Admin Settings Panel
      </h1>

      {/* 🔒 GLOBAL TOGGLES */}
      <div className="grid gap-6 md:grid-cols-2 mb-10">

        {/* ORDER LOCK */}
        <div className="bg-[#111827] p-5 rounded-xl shadow-lg flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Lock size={18} /> Order System Lock
            </h2>
            <p className="text-gray-400 text-sm">Disable all user orders.</p>
          </div>

          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={orderLock}
              onChange={(e) => setOrderLock(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-[#6c63ff] transition"></div>
            <div
              className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                orderLock ? "translate-x-5" : ""
              }`}
            ></div>
          </label>
        </div>

        {/* SIGNUP LOCK */}
        <div className="bg-[#111827] p-5 rounded-xl shadow-lg flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <UserPlus size={18} /> Signup Access
            </h2>
            <p className="text-gray-400 text-sm">Enable/disable new accounts.</p>
          </div>

          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!signupLock}
              onChange={(e) => setSignupLock(!e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-[#6c63ff] transition"></div>
            <div
              className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                !signupLock ? "translate-x-5" : ""
              }`}
            ></div>
          </label>
        </div>
      </div>

      {/* 📢 ANNOUNCEMENT */}
      <div className="bg-[#111827] p-6 rounded-xl shadow-lg mb-10">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
          <Bell size={18} /> Announcement Message
        </h2>
        <textarea
          value={announcement}
          onChange={(e) => setAnnouncement(e.target.value)}
          className="w-full h-32 bg-[#1b2138] border border-gray-600 rounded-md px-3 py-2 text-white resize-none"
        ></textarea>
      </div>

      {/* 💳 PAYMENT SETTINGS – With Instructions + Edit */}
<div className="bg-[#111827] p-6 rounded-xl shadow-lg mb-10">
  <h2 className="text-lg font-semibold mb-4">Payment Numbers & Instructions</h2>

  {/* BKASH */}
<div className="mb-6 p-4 bg-[#1b2138] rounded-lg border border-gray-700">

  <div className="flex justify-between items-center mb-3">
  <h3 className="font-semibold text-pink-400">📌 Bkash</h3>

  <div className="flex gap-2">
    {/* EDIT BUTTON */}
    <button
      onClick={() => setBkashEdit(!bkashEdit)}
      className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-700 text-sm"
    >
      {bkashEdit ? "Close" : "Edit"}
    </button>

    {/* ON/OFF Switch */}
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={bkashActive}
        onChange={(e) => setBkashActive(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-12 h-6 bg-gray-600 peer-checked:bg-green-500 rounded-full"></div>
      <div
        className={`w-5 h-5 bg-white rounded-full absolute top-0.5 left-1 transition ${
          bkashActive ? "translate-x-6" : ""
        }`}
      ></div>
    </label>
  </div>

    {/* ON / OFF Switch */}
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={bkashActive}
        onChange={(e) => setBkashActive(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-12 h-6 bg-gray-600 peer-checked:bg-green-500 rounded-full"></div>
      <div
        className={`w-5 h-5 bg-white rounded-full absolute top-0.5 left-1 transition ${
          bkashActive ? "translate-x-6" : ""
        }`}
      ></div>
    </label>
  </div>

  {/* EDIT MODE */}
  <div>
    <input
      value={bkashNum}
      onChange={(e) => setBkashNum(e.target.value)}
      disabled={!bkashActive || !bkashEdit}
      placeholder="Bkash Number"
      className="w-full mb-3 bg-[#0f1530] border border-gray-600 rounded-md px-3 py-3 text-white"
    />

    <textarea
      value={bkashInstruction}
      onChange={(e) => setBkashInstruction(e.target.value)}
      disabled={!bkashActive}
      placeholder="Bkash instruction..."
      className="w-full bg-[#0f1530] border border-gray-600 rounded-md px-3 py-3 text-white h-32 resize-none"
    ></textarea>
  </div>
</div>

  {/* NAGAD */}
<div className="mb-6 p-4 bg-[#1b2138] rounded-lg border border-gray-700">

  <div className="flex justify-between items-center mb-3">
    <h3 className="font-semibold text-orange-400">📌 Nagad</h3>

    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={nagadActive}
        onChange={(e) => setNagadActive(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-12 h-6 bg-gray-600 peer-checked:bg-green-500 rounded-full"></div>
      <div
        className={`w-5 h-5 bg-white rounded-full absolute top-0.5 left-1 transition ${
          nagadActive ? "translate-x-6" : ""
        }`}
      ></div>
    </label>
  </div>

  <input
    value={nagadNum}
    onChange={(e) => setNagadNum(e.target.value)}
    disabled={!nagadActive}
    placeholder="Nagad Number"
    className="w-full mb-3 bg-[#0f1530] border border-gray-600 rounded-md px-3 py-3 text-white"
  />

  <textarea
    value={nagadInstruction}
    onChange={(e) => setNagadInstruction(e.target.value)}
    disabled={!nagadActive}
    placeholder="Nagad instruction..."
    className="w-full bg-[#0f1530] border border-gray-600 rounded-md px-3 py-3 text-white h-32 resize-none"
  ></textarea>
</div>

  {/* ROCKET */}
<div className="mb-6 p-4 bg-[#1b2138] rounded-lg border border-gray-700">

  <div className="flex justify-between items-center mb-3">
    <h3 className="font-semibold text-purple-400">📌 Rocket</h3>

    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={rocketActive}
        onChange={(e) => setRocketActive(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-12 h-6 bg-gray-600 peer-checked:bg-green-500 rounded-full"></div>
      <div
        className={`w-5 h-5 bg-white rounded-full absolute top-0.5 left-1 transition ${
          rocketActive ? "translate-x-6" : ""
        }`}
      ></div>
    </label>
  </div>
  </div>

  <input
    value={rocketNum}
    onChange={(e) => setRocketNum(e.target.value)}
    disabled={!rocketActive}
    placeholder="Rocket Number"
    className="w-full mb-3 bg-[#0f1530] border border-gray-600 rounded-md px-3 py-3 text-white"
  />

  <textarea
    value={rocketInstruction}
    onChange={(e) => setRocketInstruction(e.target.value)}
    disabled={!rocketActive}
    placeholder="Rocket instruction..."
    className="w-full bg-[#0f1530] border border-gray-600 rounded-md px-3 py-3 text-white h-32 resize-none"
  ></textarea>
</div>

      {/* SAVE BUTTON */}
      <div className="flex justify-center">
        <button
          onClick={handleSave}
          className="bg-[#6c63ff] hover:bg-[#5848e2] transition px-8 py-3 rounded-md text-lg font-semibold"
        >
          Save Settings
        </button>
      </div>

      {savedMessage && (
        <p className="text-center mt-4 text-green-400 animate-pulse">
          {savedMessage}
        </p>
      )}
    </div>
  );
}